/**
 * Composição corporal por dobras cutâneas (adipometria). Módulo puro, sem
 * React e sem imports, para poder ser testado isolado.
 *
 * Fluxo: soma das dobras do protocolo -> densidade corporal (equação do
 * protocolo, por sexo e idade) -> % de gordura pela equação de Siri ->
 * massa gorda e massa magra a partir do peso.
 *
 * Referências:
 * - Jackson AS, Pollock ML. Generalized equations for predicting body density
 *   of men. Br J Nutr. 1978;40(3):497-504. (3 e 7 dobras, homens)
 * - Jackson AS, Pollock ML, Ward A. Generalized equations for predicting body
 *   density of women. Med Sci Sports Exerc. 1980;12(3):175-181. (3 e 7 dobras, mulheres)
 * - Durnin JVGA, Womersley J. Body fat assessed from total body density and its
 *   estimation from skinfold thickness: measurements on 481 men and women aged
 *   from 16 to 72 years. Br J Nutr. 1974;32(1):77-97. (4 dobras, tabela por faixa etária)
 * - Siri WE. Body composition from fluid spaces and density: analysis of
 *   methods. In: Brozek J, Henschel A (eds). Techniques for measuring body
 *   composition. Washington: National Academy of Sciences; 1961. p. 223-244.
 *
 * Sexo: 'male' | 'female' (a variante da silhueta, que vem de gender '0'/'1').
 * Idade em anos completos na data da avaliação.
 */

export const SKINFOLD_PROTOCOL = Object.freeze({
  JP3: 'jp3',
  JP7: 'jp7',
  DW4: 'dw4',
});

const JP7_FOLDS = ['skinfold_chest', 'skinfold_midaxillary', 'skinfold_triceps', 'skinfold_subscapular', 'skinfold_abdominal', 'skinfold_suprailiac', 'skinfold_thigh'];

/**
 * `folds`: chaves das dobras (mm) somadas pelo protocolo, por sexo.
 * `minAge`: idade mínima da amostra original (abaixo disso não se calcula).
 */
export const SKINFOLD_PROTOCOLS = Object.freeze([
  {
    key: SKINFOLD_PROTOCOL.JP3,
    label: 'Jackson-Pollock 3 dobras',
    folds: {
      male: ['skinfold_chest', 'skinfold_abdominal', 'skinfold_thigh'],
      female: ['skinfold_triceps', 'skinfold_suprailiac', 'skinfold_thigh'],
    },
    minAge: 18,
  },
  {
    key: SKINFOLD_PROTOCOL.JP7,
    label: 'Jackson-Pollock 7 dobras',
    folds: { male: JP7_FOLDS, female: JP7_FOLDS },
    minAge: 18,
  },
  {
    key: SKINFOLD_PROTOCOL.DW4,
    label: 'Durnin-Womersley 4 dobras',
    folds: {
      male: ['skinfold_biceps', 'skinfold_triceps', 'skinfold_subscapular', 'skinfold_suprailiac'],
      female: ['skinfold_biceps', 'skinfold_triceps', 'skinfold_subscapular', 'skinfold_suprailiac'],
    },
    minAge: 17,
  },
]);

export function getSkinfoldProtocol(key) {
  return SKINFOLD_PROTOCOLS.find((protocol) => protocol.key === key) || null;
}

/**
 * Dobras pedidas pelo protocolo. Sem sexo definido, a união das listas dos
 * dois sexos (o formulário mostra todas e o cálculo explica o que falta).
 */
export function protocolFolds(protocolKey, sex) {
  const protocol = getSkinfoldProtocol(protocolKey);
  if (!protocol) return [];
  if (protocol.folds[sex]) return protocol.folds[sex];
  return [...new Set([...protocol.folds.male, ...protocol.folds.female])];
}

// Durnin & Womersley (1974), tabela 4 (soma de bíceps, tríceps, subescapular e
// suprailíaca): D = c - m * log10(soma). `to` = idade máxima da faixa.
const DURNIN_WOMERSLEY = {
  male: [
    { to: 19, c: 1.1620, m: 0.0630 },
    { to: 29, c: 1.1631, m: 0.0632 },
    { to: 39, c: 1.1422, m: 0.0544 },
    { to: 49, c: 1.1620, m: 0.0700 },
    { to: Infinity, c: 1.1715, m: 0.0779 },
  ],
  female: [
    { to: 19, c: 1.1549, m: 0.0678 },
    { to: 29, c: 1.1599, m: 0.0717 },
    { to: 39, c: 1.1423, m: 0.0632 },
    { to: 49, c: 1.1333, m: 0.0612 },
    { to: Infinity, c: 1.1339, m: 0.0645 },
  ],
};

/** Densidade corporal (g/mL) pela equação do protocolo. `sum` em mm. */
export function bodyDensity(protocolKey, sex, age, sum) {
  if (sex !== 'male' && sex !== 'female') return null;
  if (!Number.isFinite(age) || !Number.isFinite(sum) || sum <= 0) return null;

  switch (protocolKey) {
    case SKINFOLD_PROTOCOL.JP3:
      // Homens: peitoral + abdominal + coxa. Mulheres: tríceps + suprailíaca + coxa.
      return sex === 'male'
        ? 1.10938 - 0.0008267 * sum + 0.0000016 * sum * sum - 0.0002574 * age
        : 1.0994921 - 0.0009929 * sum + 0.0000023 * sum * sum - 0.0001392 * age;
    case SKINFOLD_PROTOCOL.JP7:
      return sex === 'male'
        ? 1.112 - 0.00043499 * sum + 0.00000055 * sum * sum - 0.00028826 * age
        : 1.097 - 0.00046971 * sum + 0.00000056 * sum * sum - 0.00012828 * age;
    case SKINFOLD_PROTOCOL.DW4: {
      const row = DURNIN_WOMERSLEY[sex].find((item) => age <= item.to);
      return row.c - row.m * Math.log10(sum);
    }
    default:
      return null;
  }
}

/** % de gordura pela equação de Siri (1961): (4,95 / D - 4,50) x 100. */
export function siriBodyFat(density) {
  if (!Number.isFinite(density) || density <= 0) return null;
  return (4.95 / density - 4.5) * 100;
}

/**
 * Idade em anos completos em `date`. `birthday` aceita 'AAAA-MM-DD' (com ou
 * sem hora) e 'DD/MM/AAAA', os dois formatos que existem no cadastro.
 */
export function ageAt(birthday, date) {
  if (!birthday || typeof birthday !== 'string') return null;
  let year;
  let month;
  let day;
  if (birthday.includes('/')) {
    [day, month, year] = birthday.split('/').map(Number);
  } else {
    [year, month, day] = birthday.slice(0, 10).split('-').map(Number);
  }
  if (!year || !month || !day || year < 1900) return null;

  const ref = date ? new Date(String(date).replace(' ', 'T')) : new Date();
  if (Number.isNaN(ref.getTime())) return null;

  let age = ref.getFullYear() - year;
  const beforeBirthday = ref.getMonth() + 1 < month || (ref.getMonth() + 1 === month && ref.getDate() < day);
  if (beforeBirthday) age -= 1;
  return age >= 0 && age < 130 ? age : null;
}

const round = (value, decimals) => Math.round(value * Math.pow(10, decimals)) / Math.pow(10, decimals);

/**
 * Calcula a composição corporal. Entradas:
 * `protocol` (chave), `sex`, `age` (anos), `weight` (kg, opcional),
 * `folds` ({ chave: mm }).
 *
 * Devolve { ok: true, protocol, sum, density, bodyFat, fatMass, leanMass }
 * (massas null sem peso) ou { ok: false, reason } explicando o que falta.
 */
export function computeBodyComposition({ protocol: protocolKey, sex, age, weight, folds }) {
  const protocol = getSkinfoldProtocol(protocolKey);
  if (!protocol) return { ok: false, reason: 'Escolha o protocolo das dobras.' };
  if (sex !== 'male' && sex !== 'female') {
    return { ok: false, reason: 'Informe o sexo no cadastro do paciente para calcular.' };
  }
  if (age === null || age === undefined || !Number.isFinite(age)) {
    return { ok: false, reason: 'Informe a data de nascimento no cadastro do paciente para calcular.' };
  }
  if (age < protocol.minAge) {
    return { ok: false, reason: `Protocolo validado a partir de ${protocol.minAge} anos.` };
  }

  const keys = protocol.folds[sex];
  const missing = keys.filter((key) => !(Number(folds && folds[key]) > 0));
  if (missing.length > 0) return { ok: false, reason: 'missing', missing };

  const sum = keys.reduce((total, key) => total + Number(folds[key]), 0);
  const density = bodyDensity(protocol.key, sex, age, sum);
  const bodyFat = siriBodyFat(density);
  // Fora disso a equação saiu do domínio (dobras incoerentes): não mostra número
  if (bodyFat === null || bodyFat < 2 || bodyFat > 70) {
    return { ok: false, reason: 'Resultado fora da faixa plausível: confira as dobras.' };
  }

  const hasWeight = Number.isFinite(weight) && weight > 0;
  const fatMass = hasWeight ? (weight * bodyFat) / 100 : null;
  return {
    ok: true,
    protocol: protocol.key,
    sum: round(sum, 1),
    density: round(density, 4),
    bodyFat: round(bodyFat, 1),
    fatMass: hasWeight ? round(fatMass, 1) : null,
    leanMass: hasWeight ? round(weight - fatMass, 1) : null,
  };
}
